/**
 * Universal Drag Scroll for Siccas Components
 *
 * This script enables horizontal scrolling by clicking and dragging the content,
 * mimicking the behavior of a scrollbar. It improves user experience by allowing
 * a smooth and intuitive way to navigate through horizontal content.
 *
 * Features:
 * - Works with both static and dynamically loaded content via AJAX
 * - Prevents duplicate initialization of containers
 * - Handles link clicks properly (prevents accidental navigation during drag)
 * - Adds visual feedback during dragging with the "is-dragging" class
 * - Uses MutationObserver to detect and initialize newly added elements
 *
 * Usage:
 * Add the class "drag-and-drop-slide" to any container that should have
 * horizontal drag-to-scroll functionality.
 */

document.addEventListener("DOMContentLoaded", function () {
  // Keep track of initialized containers to avoid duplicates
  const initializedContainers = new WeakSet();

  // Main function to initialize drag-and-drop for a container
  function initDragScroll(scrollContainer) {
    // Skip if already initialized
    if (initializedContainers.has(scrollContainer)) return;

    // Mark as initialized
    initializedContainers.add(scrollContainer);

    let isDown = false;
    let startX;
    let scrollLeft;
    let moved = false;

    // Prevent links from being draggable
    scrollContainer.querySelectorAll("a").forEach((link) => {
      link.draggable = false;
    });

    // Mouse down event
    scrollContainer.addEventListener("mousedown", (e) => {
      if (e.target.tagName === "A") e.preventDefault();
      isDown = true;
      moved = false;
      scrollContainer.classList.add("is-dragging");
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    });

    // Mouse leave event
    scrollContainer.addEventListener("mouseleave", () => {
      isDown = false;
      scrollContainer.classList.remove("is-dragging");
    });

    // Mouse up event
    scrollContainer.addEventListener("mouseup", () => {
      isDown = false;
      scrollContainer.classList.remove("is-dragging");
    });

    // Mouse move event
    scrollContainer.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 1.5;
      if (Math.abs(walk) > 5) moved = true;
      scrollContainer.scrollLeft = scrollLeft - walk;
    });

    // Prevent link clicks if dragging occurred
    scrollContainer.querySelectorAll("a").forEach((link) => {
      link.addEventListener("click", (e) => {
        if (moved) {
          e.preventDefault();
        }
      });
    });
  }

  // Initialize all existing drag-and-drop containers
  function initAllContainers() {
    const scrollContainers = document.querySelectorAll(".drag-and-drop-slide");
    scrollContainers.forEach(initDragScroll);
  }

  // Initialize existing containers first
  initAllContainers();

  // Set up observer for dynamically added containers
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.addedNodes.length) {
        // Check if any of the added nodes are drag containers or contain them
        mutation.addedNodes.forEach(function (node) {
          // Check if node is an element
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the node itself is a drag container
            if (
              node.classList &&
              node.classList.contains("drag-and-drop-slide")
            ) {
              initDragScroll(node);
            }

            // Check if the node contains drag containers
            if (node.querySelectorAll) {
              const containers = node.querySelectorAll(".drag-and-drop-slide");
              containers.forEach(initDragScroll);
            }
          }
        });
      }
    });
  });

  // Start observing the entire document
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

/**
 * Universal Slider Navigation for Siccas Components
 *
 * Adds prev/next arrow buttons to horizontal sliders.
 * Buttons show/hide automatically based on scroll position.
 *
 * DATA ATTRIBUTES:
 * - data-slider-nav        : Container wrapper (required)
 * - data-slider-nav-target : Scrollable element inside wrapper (required)
 * - data-slider-nav-prev   : "Previous" button (required)
 * - data-slider-nav-next   : "Next" button (required)
 *
 * HTML EXAMPLE:
 * <div class="my-slider-wrapper" data-slider-nav>
 *   <button class="my-nav my-nav--prev is-hidden" data-slider-nav-prev>←</button>
 *   <ul class="my-slider" data-slider-nav-target>...</ul>
 *   <button class="my-nav my-nav--next is-hidden" data-slider-nav-next>→</button>
 * </div>
 *
 * BEHAVIOR:
 * - Prev button hidden when at start
 * - Next button hidden when at end
 * - Both buttons hidden if no overflow (content fits)
 * - Click scrolls 80% of visible width
 * - Uses requestAnimationFrame for performance
 * - MutationObserver for dynamically added elements
 *
 * CSS CLASSES:
 * - .is-hidden : Added/removed by script to show/hide buttons
 *
 * DEPENDENCIES:
 * - Universal styles in siccas-theme.css (position, z-index, themes)
 * - SVG icons in siccas-icons-sprite.liquid (#icon-slider-prev, #icon-slider-next)
 */

document.addEventListener("DOMContentLoaded", function () {
  const initializedNavs = new WeakSet();

  function initSliderNav(wrapper) {
    if (initializedNavs.has(wrapper)) return;
    initializedNavs.add(wrapper);

    const slider = wrapper.querySelector("[data-slider-nav-target]");
    const prevBtn = wrapper.querySelector("[data-slider-nav-prev]");
    const nextBtn = wrapper.querySelector("[data-slider-nav-next]");

    if (!slider || !prevBtn || !nextBtn) return;

    let ticking = false;

    function updateButtons() {
      const hasOverflow = slider.scrollWidth > slider.clientWidth;
      const atStart = slider.scrollLeft <= 0;
      const atEnd = slider.scrollLeft + slider.clientWidth >= slider.scrollWidth - 1;

      if (!hasOverflow) {
        prevBtn.classList.add("is-hidden");
        nextBtn.classList.add("is-hidden");
        return;
      }

      if (atStart) {
        prevBtn.classList.add("is-hidden");
      } else {
        prevBtn.classList.remove("is-hidden");
      }

      if (atEnd) {
        nextBtn.classList.add("is-hidden");
      } else {
        nextBtn.classList.remove("is-hidden");
      }
    }

    slider.addEventListener("scroll", function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          updateButtons();
          ticking = false;
        });
        ticking = true;
      }
    });

    prevBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      const scrollAmount = slider.clientWidth * 0.8;
      slider.scrollBy({ left: -scrollAmount, behavior: "smooth" });
    });

    nextBtn.addEventListener("click", function (e) {
      e.stopPropagation();
      const scrollAmount = slider.clientWidth * 0.8;
      slider.scrollBy({ left: scrollAmount, behavior: "smooth" });
    });

    // For mega-menu: update buttons when <details> opens
    const details = wrapper.closest("details");
    if (details) {
      details.addEventListener("toggle", function () {
        if (details.open) {
          // Small delay to allow CSS transitions to complete
          setTimeout(updateButtons, 100);
        }
      });
    }

    updateButtons();
  }

  function initAllNavs() {
    document.querySelectorAll("[data-slider-nav]").forEach(initSliderNav);
  }

  initAllNavs();

  const navObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType === Node.ELEMENT_NODE) {
            if (node.hasAttribute && node.hasAttribute("data-slider-nav")) {
              initSliderNav(node);
            }
            if (node.querySelectorAll) {
              node.querySelectorAll("[data-slider-nav]").forEach(initSliderNav);
            }
          }
        });
      }
    });
  });

  navObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

/**
 * Remove trailing zeros from prices (e.g., "20.00 €" → "20 €").
 *
 * It runs in the following cases:
 * 1. On initial page load.
 * 2. When a product variant is changed (listens to 'variant:change' event).
 * 3. Once when the <product-recommendations> element becomes visible in the viewport.
 *
 * It targets elements with the classes '.price-item' and '.save-amount'.
 * The script uses MutationObserver to wait for <product-recommendations> to appear in the DOM,
 * then IntersectionObserver to detect its visibility and apply the cleanup only once.
 */
/*
document.addEventListener("DOMContentLoaded", function () {
  function removeTrailingZeros() {
    const priceElements = document.querySelectorAll(
      ".price-item, .save-amount"
    );
    priceElements.forEach(function (element) {
      const text = element.textContent.trim();
      element.textContent = text.replace(
        /([0-9]+)[,.][0]{2}\s*([€$£₽])/g,
        "$1 $2"
      );
    });
  }

  // Trigger on page load
  removeTrailingZeros();

  // Trigger on variant change
  document.addEventListener("variant:change", removeTrailingZeros);

  // Observe DOM for <product-recommendations> being added
  const waitForRecommendations = new MutationObserver((mutations, observer) => {
    const recommendationsSection = document.querySelector(
      "product-recommendations"
    );
    if (recommendationsSection) {
      const observerVisible = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              removeTrailingZeros();
              obs.disconnect(); // stop observing visibility
            }
          });
        },
        {
          root: null,
          threshold: 0.1,
        }
      );

      observerVisible.observe(recommendationsSection);
      observer.disconnect(); // stop observing DOM
    }
  });

  waitForRecommendations.observe(document.body, {
    childList: true,
    subtree: true,
  });
});
*/
